import { Controller } from "@hotwired/stimulus"

// Polls the current page and refreshes the messages list while a response is processing.
// Starts polling when:
//   1. A processing indicator is found in the DOM
//   2. A form submission adds new content (a response is expected)
// Stops polling once no processing indicators remain after several checks.
//
// Usage:
//   <div data-controller="poll">
//     <div id="messages_list">...</div>
//   </div>
export default class extends Controller {
  connect() {
    this.polling = false
    this.emptyChecks = 0
    this.checkAndPoll()

    // Watch for DOM changes (e.g. turbo stream appending a user message after form submit)
    this.observer = new MutationObserver(() => {
      // Any DOM mutation in the messages area means something happened — start polling
      // because a background job is likely about to create an assistant message
      this.emptyChecks = 0
      if (!this.polling) {
        this.startPolling()
      }
    })

    const messagesList = this.element.querySelector("#messages_list")
    if (messagesList) {
      this.observer.observe(messagesList, { childList: true, subtree: true })
    }
  }

  disconnect() {
    this.stopPolling()
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  checkAndPoll() {
    const hasProcessing = this.element.querySelector('[data-status="processing"]') !== null
    const hasOnlyUserMessages = this.element.querySelectorAll('[data-role="assistant"]').length === 0
      && this.element.querySelectorAll('[data-role="user"]').length > 0
    if ((hasProcessing || hasOnlyUserMessages) && !this.polling) {
      this.startPolling()
    }
  }

  startPolling() {
    if (this.polling) return
    this.polling = true
    this.emptyChecks = 0
    // Poll immediately, then every 3 seconds
    this.poll()
    this.timer = setInterval(() => this.poll(), 3000)
  }

  stopPolling() {
    this.polling = false
    this.emptyChecks = 0
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  async poll() {
    try {
      const response = await fetch(window.location.href, {
        headers: { "Accept": "text/html" }
      })

      if (!response.ok) return

      const html = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")

      const newMessages = doc.querySelector("#messages_list")
      const currentMessages = document.querySelector("#messages_list")

      if (newMessages && currentMessages) {
        currentMessages.innerHTML = newMessages.innerHTML
      }

      // Check if there's still something processing
      const stillProcessing = doc.querySelector('[data-status="processing"]') !== null
      if (!stillProcessing) {
        this.emptyChecks++
        // Give the job a few poll cycles to create the assistant message before giving up
        if (this.emptyChecks > 5) {
          this.stopPolling()
        }
      } else {
        this.emptyChecks = 0
      }
    } catch (e) {
      // Network error, keep trying
    }
  }
}
